import { PropsWithChildren, useEffect, useMemo, useState } from 'react';
import createSafeContext from '@paradigm/utils/createSafeContext';
import { useRfqFiltersFormView } from '@paradigm/features/src/unified-rfqs/ui/rfq-filters/form-context';
import { useRfqFiltersModalMethods } from '@paradigm/features/src/unified-rfqs/ui/rfq-filters/modal-context';
import { FormView } from './form-view';
import { useTabsActions, useTabsView } from './tabs-context';

/**
 * Also the shape a stored filter is merged onto: filters persist across
 * releases and are never migrated, so one saved before a field existed comes
 * back without it.
 */
export const INITIAL_STATE: FilterViewState = {
  productCodes: [],
  auctionTypes: [],
  assets: [],
  instruments: [],
  venues: [],
  products: [],
  strategies: [],
  strategyCodes: [],
  notional: { min: '', max: '' },
  counterparties: [],
};

export interface FilterViewState extends Pick<FormView, 'productCodes'> {
  readonly auctionTypes: FormView['auctionTypes']['selectedOptions'];
  readonly assets: FormView['assets']['selectedOptions'];
  readonly instruments: FormView['instruments']['selectedOptions'];
  readonly venues: FormView['venues']['selectedOptions'];
  readonly products: FormView['products']['selectedOptions'];
  readonly strategies: FormView['strategies']['selectedOptions'];
  readonly notional: FormView['notional'];
  readonly strategyCodes: string[];
  readonly counterparties: FormView['counterparties']['selectedOptions'];
}

/**
 * Whether the user narrowed by product in a way no active product satisfies —
 * an asset with no market on the selected venue, say. The resolved
 * `productCodes` are empty either way, so without this the combination reads
 * as "no product filter" and matches everything instead of nothing.
 */
export const hasConflictingProductSelection = (state: FilterViewState) =>
  state.assets.length +
    state.instruments.length +
    state.products.length +
    state.venues.length >
    0 && state.productCodes.length === 0;

interface FiltersValue {
  readonly state: FilterViewState;
  readonly activeFiltersCount: number;
  readonly submitFilter: () => void;
}

export const [useRfqFiltersValue, FiltersValueProvider] =
  createSafeContext<FiltersValue>('RfqFiltersValue');

export default function RfqFiltersValueProvider({
  children,
}: PropsWithChildren) {
  const { activeTab } = useTabsView();
  const { setTab } = useTabsActions();
  const [state, set] = useState<FilterViewState>(INITIAL_STATE);
  const form = useRfqFiltersFormView();
  const { close } = useRfqFiltersModalMethods();

  useEffect(() => {
    // since we're adding new strategy filter we need to make sure we hidrate with initial state value for old filters
    set({ ...INITIAL_STATE, ...activeTab.filter });
  }, [activeTab]);

  const view: FiltersValue = useMemo(
    () => ({
      state,
      activeFiltersCount:
        state.assets.length +
        state.instruments.length +
        state.auctionTypes.length +
        state.venues.length +
        state.products.length +
        state.strategies.length +
        state.counterparties.length +
        (state.notional.min !== '' || state.notional.max !== '' ? 1 : 0),

      submitFilter: () => {
        const filterValue: FilterViewState = {
          notional: form.notional,
          productCodes: form.productCodes,
          auctionTypes: form.auctionTypes.selectedOptions,
          assets: form.assets.selectedOptions,
          instruments: form.instruments.selectedOptions,
          venues: form.venues.selectedOptions,
          products: form.products.selectedOptions,
          strategies: form.strategies.selectedOptions,
          strategyCodes: form.strategies.selectedOptions.map((s) => s.value),
          counterparties: form.counterparties.selectedOptions,
        };
        set(filterValue);
        // Update current tab filter, whenever a new filter is submitted
        setTab({ ...activeTab, filter: filterValue });

        close();
      },
    }),
    [
      state,
      form.notional,
      form.productCodes,
      form.auctionTypes.selectedOptions,
      form.assets.selectedOptions,
      form.instruments.selectedOptions,
      form.venues.selectedOptions,
      form.products.selectedOptions,
      form.strategies.selectedOptions,
      form.counterparties.selectedOptions,
      setTab,
      activeTab,
      close,
    ],
  );

  return <FiltersValueProvider value={view}>{children}</FiltersValueProvider>;
}
